export interface Opportunity {
  id: string;
  source: string;
  source_id: string;
  title: string;
  description: string;
  url: string;
  company_name?: string;
  location?: string;
  posted_at?: string;
  raw_data: Record<string, unknown>;
  match_score: number;
  status: string;
  fetched_at: string;
  enriched_at?: string;
  matched_at?: string;
  created_at: string;
  updated_at: string;
  tech_stack?: string[];
  pain_points?: string[];
}

export interface Pitch {
  id: string;
  opportunity_id: string;
  company_id?: string;
  pitch_type: string;
  subject: string;
  body: string;
  contact_email?: string;
  contact_name?: string;
  match_score: number;
  matched_skills: string[];
  status: string;
  human_feedback?: string;
  sent_at?: string;
  created_at: string;
  updated_at: string;
}

export interface Company {
  id: string;
  name: string;
  website?: string;
  industry?: string;
  size?: string;
  location?: string;
  description?: string;
  tech_stack: string[];
  pain_points: string[];
  contact_emails: string[];
  contact_phones: string[];
  source: string;
  source_url: string;
  last_enriched: string;
  created_at: string;
  updated_at: string;
}

export interface Stats {
  opportunities_by_status: Array<{ status: string; count: number }>;
  pitches_by_status: Array<{ status: string; count: number }>;
  total_companies: number;
  top_sources: Array<{ source: string; count: number }>;
  recent_fetch_status: Array<{ status: string; count: number }>;
}

export function formatDate(dateString: string): string {
  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit"
  }).format(new Date(dateString));
}

export function formatRelativeTime(dateString: string): string {
  const diffMs = Date.now() - new Date(dateString).getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);
  
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric" }).format(new Date(dateString));
}

export function getStatusColor(status: string): string {
  const colors: Record<string, string> = {
    new: "bg-blue-100 text-blue-800",
    enriched: "bg-purple-100 text-purple-800",
    matched: "bg-green-100 text-green-800",
    pitched: "bg-amber-100 text-amber-800",
    applied: "bg-blue-100 text-blue-800",
    rejected: "bg-red-100 text-red-800",
    archived: "bg-slate-100 text-slate-800",
    draft: "bg-slate-100 text-slate-800",
    approved: "bg-green-100 text-green-800",
    sent: "bg-blue-100 text-blue-800",
    bounced: "bg-red-100 text-red-800",
    replied: "bg-green-100 text-green-800"
  };
  return colors[status] || "bg-slate-100 text-slate-800";
}

export function getPitchTypeLabel(type: string): string {
  const labels: Record<string, string> = {
    job_application: "Job Application",
    contract_proposal: "Contract Proposal",
    module_replacement: "Module Replacement",
    partnership: "Partnership",
    scholarship: "Scholarship"
  };
  return labels[type] || type;
}

export function getPitchTypeColor(type: string): string {
  const colors: Record<string, string> = {
    job_application: "bg-blue-100 text-blue-800",
    contract_proposal: "bg-purple-100 text-purple-800",
    module_replacement: "bg-orange-100 text-orange-800",
    partnership: "bg-green-100 text-green-800",
    scholarship: "bg-purple-100 text-purple-800"
  };
  return colors[type] || "bg-slate-100 text-slate-800";
}

export function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).trim() + "...";
}

export function getMatchScoreColor(score: number): string {
  if (score >= 0.8) return "text-green-600";
  if (score >= 0.65) return "text-amber-600";
  return "text-slate-600";
}

export function getMatchScoreBg(score: number): string {
  if (score >= 0.8) return "bg-green-100";
  if (score >= 0.65) return "bg-amber-100";
  return "bg-slate-100";
}

export function cn(...classes: (string | boolean | undefined | null)[]): string {
  return classes.filter(Boolean).join(" ");
}